import { Injectable, inject } from '@angular/core';
import {
  Timestamp,
  collection,
  collectionGroup,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { BusinessCsf, BusinessCsfStatus } from '../../models/business-csf.type';
import { isCfdiUseCode } from '../../models/user-profile.type';
import { FIREBASE_FIRESTORE } from '../../firebase/firebase.providers';
import { MembershipService } from '../auth/membership.service';
import { CallableFunctionsService } from '../functions/callable-functions.service';
import { FirebaseStorageService } from '../storage/firebase-storage.service';

const MAX_PDF_BYTES = 15 * 1024 * 1024;

@Injectable({ providedIn: 'root' })
export class BusinessCsfService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private storage = inject(FirebaseStorageService);
  private membership = inject(MembershipService);
  private callables = inject(CallableFunctionsService);

  watch(
    businessId: string,
    onValue: (rows: BusinessCsf[]) => void,
    onError?: (err: unknown) => void
  ): () => void {
    return onSnapshot(
      collection(this.firestore, 'businesses', businessId, 'csfs'),
      (snap) => {
        onValue(sortCsfs(snap.docs.map((item) => parseBusinessCsf(item.id, item.data()))));
      },
      (err) => onError?.(err)
    );
  }

  async listPending(): Promise<BusinessCsf[]> {
    const snap = await getDocs(
      query(collectionGroup(this.firestore, 'csfs'), where('status', '==', 'pending'))
    );
    return sortCsfs(snap.docs.map((item) => parseBusinessCsf(item.id, item.data()))).filter(
      (row) => !!row.invoicingTenantId
    );
  }

  async list(businessId: string): Promise<BusinessCsf[]> {
    const snap = await getDocs(collection(this.firestore, 'businesses', businessId, 'csfs'));
    return sortCsfs(snap.docs.map((item) => parseBusinessCsf(item.id, item.data())));
  }

  async upload(input: {
    constancia?: File | null;
    certificate: File;
    privateKey: File;
    privateKeyPassword: string;
    rfc?: string;
    nombre?: string;
    apellidoPaterno?: string;
    apellidoMaterno?: string;
    regimen?: string;
    postalCode?: string;
  }): Promise<string> {
    if (!this.membership.canUploadFiscalData()) {
      throw new Error('Solo el propietario o un doctor puede subir datos de facturación.');
    }
    if (input.constancia) {
      if (input.constancia.type !== 'application/pdf' && !input.constancia.name.toLowerCase().endsWith('.pdf')) {
        throw new Error('La constancia debe ser un PDF.');
      }
      if (input.constancia.size > MAX_PDF_BYTES) {
        throw new Error('La constancia pesa más de 15 MB.');
      }
    }
    if (!input.certificate.name.toLowerCase().endsWith('.cer')) {
      throw new Error('Selecciona el archivo .cer de la e.firma.');
    }
    if (!input.privateKey.name.toLowerCase().endsWith('.key')) {
      throw new Error('Selecciona el archivo .key de la e.firma.');
    }
    const businessId = this.membership.requireSelectedBusinessId();
    const [pdfBase64, certificateBase64, privateKeyBase64] = await Promise.all([
      input.constancia ? readFileAsBase64(input.constancia) : Promise.resolve(''),
      readFileAsBase64(input.certificate),
      readFileAsBase64(input.privateKey),
    ]);
    const result = await this.callables.uploadBusinessCsf({
      businessId,
      pdfBase64,
      certificateBase64,
      privateKeyBase64,
      privateKeyPassword: input.privateKeyPassword,
      rfc: input.rfc || '',
      nombre: input.nombre || '',
      apellidoPaterno: input.apellidoPaterno || '',
      apellidoMaterno: input.apellidoMaterno || '',
      regimen: input.regimen || '',
      postalCode: input.postalCode || '',
    });
    return result.rfc;
  }

  readBytes(storagePath: string): Promise<Uint8Array> {
    return this.storage.readBytes(storagePath);
  }

  readText(storagePath: string): Promise<string> {
    return this.storage.readText(storagePath);
  }

  adminDelete(csf: BusinessCsf): Promise<void> {
    return this.callables.adminDeleteBusinessCsf({
      businessId: csf.businessId,
      rfc: csf.rfc.trim().toUpperCase(),
    });
  }

  async delete(rfc: string): Promise<void> {
    if (!this.membership.isSelectedBusinessDoctor()) {
      throw new Error('Solo un doctor puede eliminar un RFC que haya subido.');
    }
    const businessId = this.membership.requireSelectedBusinessId();
    await this.callables.deleteBusinessCsf({ businessId, rfc: rfc.trim().toUpperCase() });
  }

  async setPostalCodes(rfc: string, postalCodes: string[]): Promise<void> {
    if (!this.membership.canUploadFiscalData()) {
      throw new Error('Solo el propietario o un doctor puede actualizar los códigos postales.');
    }
    const codes = [...new Set(postalCodes.map((code) => code.trim()).filter((code) => /^(?!00000)\d{5}$/.test(code)))];
    if (codes.length > 10) {
      throw new Error('Puedes registrar hasta 10 códigos postales.');
    }
    const businessId = this.membership.requireSelectedBusinessId();
    await updateDoc(doc(this.firestore, 'businesses', businessId, 'csfs', rfc.trim().toUpperCase()), {
      postalCodes: codes,
    });
  }

  authorize(csf: BusinessCsf): Promise<void> {
    return this.callables.authorizeBusinessCsf({ businessId: csf.businessId, rfc: csf.rfc });
  }
}

function sortCsfs(rows: (BusinessCsf | null)[]): BusinessCsf[] {
  return rows
    .filter((row): row is BusinessCsf => row != null)
    .sort((a, b) => a.rfc.localeCompare(b.rfc, 'es'));
}

function parseBusinessCsf(id: string, data: Record<string, unknown>): BusinessCsf | null {
  const rfc = (typeof data['rfc'] === 'string' ? data['rfc'] : id).trim().toUpperCase();
  if (!rfc) {
    return null;
  }
  const status: BusinessCsfStatus = data['status'] === 'authorized' ? 'authorized' : 'pending';
  return {
    rfc,
    businessId: typeof data['businessId'] === 'string' ? data['businessId'] : '',
    businessName: typeof data['businessName'] === 'string' ? data['businessName'] : '',
    storagePath: typeof data['storagePath'] === 'string' ? data['storagePath'] : '',
    nombre: typeof data['nombre'] === 'string' ? data['nombre'] : '',
    apellidoPaterno: typeof data['apellidoPaterno'] === 'string' ? data['apellidoPaterno'] : '',
    apellidoMaterno: typeof data['apellidoMaterno'] === 'string' ? data['apellidoMaterno'] : '',
    regimen: typeof data['regimen'] === 'string' ? data['regimen'] : '',
    postalCodes: postalCodesFrom(data['postalCodes']),
    certificatePath: typeof data['certificatePath'] === 'string' ? data['certificatePath'] : '',
    privateKeyPath: typeof data['privateKeyPath'] === 'string' ? data['privateKeyPath'] : '',
    passwordPath: typeof data['passwordPath'] === 'string' ? data['passwordPath'] : '',
    sdgPath: typeof data['sdgPath'] === 'string' ? data['sdgPath'] : '',
    csdKeyPath: typeof data['csdKeyPath'] === 'string' ? data['csdKeyPath'] : '',
    solicitudError: typeof data['solicitudError'] === 'string' ? data['solicitudError'] : '',
    cfdiUse: isCfdiUseCode(data['cfdiUse']) ? data['cfdiUse'] : '',
    uploadedBy: typeof data['uploadedBy'] === 'string' ? data['uploadedBy'] : '',
    invoicingTenantId: typeof data['invoicingTenantId'] === 'string' ? data['invoicingTenantId'].trim() : '',
    status,
    uploadedAt: data['uploadedAt'] instanceof Timestamp ? data['uploadedAt'].toDate() : null,
  };
}

function postalCodesFrom(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return [...new Set(value.map((item) => String(item || '').trim()).filter((item) => /^(?!00000)\d{5}$/.test(item)))];
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      resolve(dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
  });
}
